import { LuBatteryLow, LuLightbulb } from 'react-icons/lu';
import { Card, IconMedallion } from '../design-system/components';

interface TokenInfo {
  tokens_available?: number;
  tokens_limit?: number;
  tokens_used?: number;
}

interface OutOfTokensPromptProps {
  tokenInfo?: TokenInfo;
  tokenType?: string;
  /** When true, display unified "credits" language instead of legacy "tokens" */
  useCredits?: boolean;
  /** The heading's id, for a dialog around the card to take its name from. */
  titleId?: string;
}

/**
 * The out-of-credits card (the quiz's oral question inline, and inside
 * OutOfTokensModal). On the design-system foundations (redesign PR23): a
 * Card with the pitch, a tip panel, the month's usage and the fine print.
 * Styles: design-system/pages/prompts.css (`.ee-prompt-pitch`).
 */
export default function OutOfTokensPrompt({
  tokenInfo,
  tokenType = 'quiz_tests',
  useCredits = false,
  titleId,
}: OutOfTokensPromptProps) {
  const {
    tokens_available: _tokens_available = 0,
    tokens_limit = 0,
    tokens_used = 0,
  } = tokenInfo || {};

  // Credits-first messaging when unified credits are available; legacy
  // token-based messaging otherwise.
  const isQuizTokens = tokenType === 'quiz_tests';
  const title = useCredits
    ? "You've Used All Your Credits"
    : isQuizTokens
      ? 'Whoa There, Quiz Enthusiast!'
      : 'Whoa There, AI Tool Enthusiast!';
  const description = useCredits
    ? "You've used all your credits for this month. Credits cover quizzes, AI tools, video calls, and more."
    : isQuizTokens
      ? "Looks like you've been practicing empathy so much, you've run out of quiz energy for this month!"
      : "Looks like you've been exploring empathy with AI so much, you've run out of AI tool energy for this month!";
  const tip = useCredits
    ? "While you wait for your credits to refresh next month, try reviewing lessons you've already completed or upgrade your plan for more credits!"
    : "While you wait for your tokens to refresh next month, try reviewing lessons you've already completed or explore our other learning resources!";

  return (
    <Card padding="lg" className="ee-prompt ee-prompt-pitch">
      <IconMedallion icon={LuBatteryLow} size="lg" />

      <h2 id={titleId} className="ee-prompt__title">
        {title}
      </h2>

      <p className="ee-prompt__lead">{description}</p>

      <div className="ee-prompt-tip">
        <p className="ee-prompt-tip__title">
          <LuLightbulb className="ee-prompt-tip__icon" aria-hidden="true" />
          <strong>Need to keep learning?</strong>
        </p>
        <p className="ee-prompt-tip__text">{tip}</p>
      </div>

      <div className="ee-prompt-stat">
        <span className="ee-prompt-stat__label">This Month&apos;s Usage</span>
        <span className="ee-prompt-stat__value">{tokens_used.toLocaleString()}</span>
        <span className="ee-prompt-stat__desc">
          of {tokens_limit.toLocaleString()} {useCredits ? 'credits' : 'tokens'} used
        </span>
      </div>

      <p className="ee-prompt__fineprint">
        You&apos;re making great progress! Keep up the compassionate learning.
      </p>
    </Card>
  );
}
